// The UI posts a liveness ping every 60 seconds while the workspace tab is alive. An abandoned
// session is finalised by the server at its last `heartbeat_at` rather than at `now`, which is
// what stops an overnight timer logging the whole night.
export const HEARTBEAT_INTERVAL_MS = 60_000;

export interface HeartbeatSchedulerDeps {
    setInterval: (handler: () => void, timeoutMs: number) => number;
    clearInterval: (handle: number) => void;
    onBeat: () => void;
}

export interface HeartbeatScheduler {
    start: () => void;
    stop: () => void;
    isRunning: () => boolean;
}

// Extracted from the React hook on purpose: a leaked interval is the realistic bug here, and
// keeping the registration rules in a plain object makes every teardown path directly testable.
//
// `start` is idempotent, so a re-render can never register a second interval; `stop` is
// idempotent too, so unmount-after-stop and tab-close-after-unmount are both safe.
export const createHeartbeatScheduler = ({
    setInterval,
    clearInterval,
    onBeat,
}: HeartbeatSchedulerDeps): HeartbeatScheduler => {
    let handle: number | null = null;

    return {
        start: () => {
            if (handle !== null) {
                // Already registered — never stack a second interval on the same session
                return;
            }

            handle = setInterval(onBeat, HEARTBEAT_INTERVAL_MS);
        },

        stop: () => {
            if (handle === null) {
                return;
            }

            clearInterval(handle);
            handle = null;
        },

        isRunning: () => handle !== null,
    };
};
